import React, { useMemo, useState } from "react";
import Icon from "@mdi/react";
import { mdiClose, mdiPlus, mdiTrashCanOutline } from "@mdi/js";
import {
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

import { PricingModelDTO, PublicMcpDTO, ToolENV } from "shared/services/McpLibraryServices";

import styles from "../styles.module.scss";

interface StdioServerConfig {
	command: string;
	args?: string[];
	env?: Record<string, string>;
	cwd?: string;
}

interface SSEServerConfig {
	url: string;
	headers?: Record<string, string>;
	authProvider?:
		| string
		| {
				type: "bearer" | "basic" | "custom";
				secretName?: string;
		  };
}

type McpServerConfig = StdioServerConfig | SSEServerConfig;

interface McpConfig {
	mcpServers: {
		[serverName: string]: McpServerConfig;
	};
}

interface McpFormState {
	slug: string;
	name: string;
	category: string;
	description: string;
	systemPrompt: string;
	deliverableTypes: string;
	iconUrl: string;
	mcpServerConfig: string;
	toolEnvFields: ToolENV[];
	pricingJunior: string;
	pricingPleno: string;
	pricingSenior: string;
}

const emptyForm: McpFormState = {
	slug: "",
	name: "",
	category: "general",
	description: "",
	systemPrompt: "",
	deliverableTypes: "",
	iconUrl: "",
	mcpServerConfig: "{}",
	toolEnvFields: [],
	pricingJunior: "",
	pricingPleno: "",
	pricingSenior: "",
};

function mcpToForm(mcp: PublicMcpDTO): McpFormState {
	return {
		slug: mcp.slug,
		name: mcp.name,
		category: mcp.category,
		description: mcp.description ?? "",
		systemPrompt: mcp.systemPrompt,
		deliverableTypes: (mcp.deliverableTypes ?? []).join(", "),
		iconUrl: mcp.iconUrl ?? "",
		mcpServerConfig: JSON.stringify(mcp.mcpServerConfig ?? {}, null, 2),
		toolEnvFields: mcp.toolEnvFields ?? [],
		pricingJunior: mcp.pricingModel?.junior?.toString() ?? "",
		pricingPleno: mcp.pricingModel?.pleno?.toString() ?? "",
		pricingSenior: mcp.pricingModel?.senior?.toString() ?? "",
	};
}

const BASE_ENV_TYPES: Array<Exclude<ToolENV["type"], `OAuth2:${string}`>> = ["string", "number", "boolean", "json"];

function getToolEnvMode(type: ToolENV["type"]): Exclude<ToolENV["type"], `OAuth2:${string}`> | "oauth2" {
	if (type.startsWith("OAuth2:")) return "oauth2";
	if (type === "string" || type === "number" || type === "boolean" || type === "json") return type;
	return "string";
}

function getOAuthProvider(type: ToolENV["type"]): string {
	return type.startsWith("OAuth2:") ? type.slice("OAuth2:".length) : "";
}

function createEmptyToolEnv(): ToolENV {
	return {
		envName: "",
		type: "string",
		label: "",
		description: "",
		required: false,
	};
}

function validateToolEnvFields(toolEnvFields: ToolENV[]): string | null {
	const envNameSet = new Set<string>();

	for (const [index, field] of toolEnvFields.entries()) {
		const envName = field.envName.trim();
		const label = field.label.trim();

		if (!envName) {
			return `Campo ENV #${index + 1}: envName é obrigatório.`;
		}

		if (!/^[A-Z][A-Z0-9_]*$/i.test(envName)) {
			return `Campo ENV #${index + 1}: envName deve conter apenas letras, números e underscore.`;
		}

		if (envNameSet.has(envName.toLowerCase())) {
			return `Campo ENV #${index + 1}: envName duplicado (${envName}).`;
		}
		envNameSet.add(envName.toLowerCase());

		if (!label) {
			return `Campo ENV #${index + 1}: label é obrigatório.`;
		}

		const validType = BASE_ENV_TYPES.includes(field.type as any) || /^OAuth2:.+$/i.test(field.type);
		if (!validType) {
			return `Campo ENV #${index + 1}: tipo inválido.`;
		}
	}

	return null;
}

function splitList(value: string): string[] {
	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

function buildPricingModel(form: McpFormState): PricingModelDTO | undefined {
	const pricingModel: PricingModelDTO = {};
	if (form.pricingJunior) pricingModel.junior = parseFloat(form.pricingJunior);
	if (form.pricingPleno) pricingModel.pleno = parseFloat(form.pricingPleno);
	if (form.pricingSenior) pricingModel.senior = parseFloat(form.pricingSenior);
	return Object.keys(pricingModel).length > 0 ? pricingModel : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStringRecord(value: unknown): value is Record<string, string> {
	if (!isRecord(value)) return false;
	return Object.values(value).every((item) => typeof item === "string");
}

function isStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function validateServerConfig(config: unknown, serverName: string): string | null {
	if (!isRecord(config)) {
		return `Servidor "${serverName}" deve ser um objeto.`;
	}

	const hasCommand = "command" in config;
	const hasUrl = "url" in config;

	if (hasCommand && hasUrl) {
		return `Servidor "${serverName}" não pode ter "command" e "url" ao mesmo tempo.`;
	}

	if (!hasCommand && !hasUrl) {
		return `Servidor "${serverName}" precisa ter "command" (stdio) ou "url" (sse).`;
	}

	if (hasCommand) {
		if (typeof config.command !== "string" || !config.command.trim()) {
			return `Servidor "${serverName}": "command" é obrigatório e deve ser string.`;
		}

		if ("args" in config && !isStringArray(config.args)) {
			return `Servidor "${serverName}": "args" deve ser string[].`;
		}

		if ("env" in config && !isStringRecord(config.env)) {
			return `Servidor "${serverName}": "env" deve ser Record<string, string>.`;
		}

		if ("cwd" in config && typeof config.cwd !== "string") {
			return `Servidor "${serverName}": "cwd" deve ser string.`;
		}
	}

	if (hasUrl) {
		if (typeof config.url !== "string" || !config.url.trim()) {
			return `Servidor "${serverName}": "url" é obrigatório e deve ser string.`;
		}

		if ("headers" in config && !isStringRecord(config.headers)) {
			return `Servidor "${serverName}": "headers" deve ser Record<string, string>.`;
		}

		if ("authProvider" in config) {
			const authProvider = config.authProvider;

			if (typeof authProvider !== "string") {
				if (!isRecord(authProvider)) {
					return `Servidor "${serverName}": "authProvider" deve ser string ou objeto.`;
				}

				if (!["bearer", "basic", "custom"].includes(String(authProvider.type))) {
					return `Servidor "${serverName}": "authProvider.type" deve ser bearer, basic ou custom.`;
				}

				if ("secretName" in authProvider && typeof authProvider.secretName !== "string") {
					return `Servidor "${serverName}": "authProvider.secretName" deve ser string.`;
				}
			}
		}
	}

	return null;
}

function parseAndValidateMcpConfig(raw: string): { value?: McpConfig; error?: string } {
	if (!raw.trim() || raw.trim() === "{}") {
		return { value: undefined };
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(raw);
	} catch {
		return { error: "MCP Server Config contém JSON inválido." };
	}

	if (!isRecord(parsed)) {
		return { error: "MCP Server Config deve ser um objeto JSON." };
	}

	if (!("mcpServers" in parsed) || !isRecord(parsed.mcpServers)) {
		return { error: 'MCP Server Config deve conter a chave "mcpServers" como objeto.' };
	}

	for (const [serverName, config] of Object.entries(parsed.mcpServers)) {
		const error = validateServerConfig(config, serverName);
		if (error) {
			return { error };
		}
	}

	const typedConfig: McpConfig = {
		mcpServers: parsed.mcpServers as McpConfig["mcpServers"],
	};

	return { value: typedConfig };
}

export interface McpFormSubmitData {
	slug?: string;
	name: string;
	category: string;
	description?: string;
	systemPrompt: string;
	deliverableTypes: string[];
	iconUrl?: string;
	toolEnvFields?: ToolENV[];
	pricingModel?: PricingModelDTO;
	mcpServerConfig?: Record<string, any>;
}

interface McpFormDialogProps {
	open: boolean;
	saving: boolean;
	editingMcp: PublicMcpDTO | null;
	onClose: () => void;
	onValidationError: (message: string) => void;
	onSubmit: (data: McpFormSubmitData) => Promise<void>;
}

export const McpFormDialog: React.FC<McpFormDialogProps> = ({ open, saving, editingMcp, onClose, onValidationError, onSubmit }) => {
	const [form, setForm] = useState<McpFormState>(emptyForm);

	React.useEffect(() => {
		if (!open) return;
		setForm(editingMcp ? mcpToForm(editingMcp) : emptyForm);
	}, [open, editingMcp]);

	const configValidation = useMemo(() => parseAndValidateMcpConfig(form.mcpServerConfig), [form.mcpServerConfig]);
	const toolEnvValidation = useMemo(() => validateToolEnvFields(form.toolEnvFields), [form.toolEnvFields]);

	const handleFormChange = (field: keyof McpFormState) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setForm((prev) => ({ ...prev, [field]: event.target.value }));
	};

	const handleToolEnvFieldChange = <K extends keyof ToolENV>(index: number, key: K, value: ToolENV[K]) => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: prev.toolEnvFields.map((field, i) => (i === index ? { ...field, [key]: value } : field)),
		}));
	};

	const handleToolEnvTypeChange = (index: number, mode: "string" | "number" | "boolean" | "json" | "oauth2") => {
		if (mode !== "oauth2") {
			handleToolEnvFieldChange(index, "type", mode);
			return;
		}
		handleToolEnvFieldChange(index, "type", "OAuth2:default");
	};

	const handleOAuthProviderChange = (index: number, provider: string) => {
		const normalizedProvider = provider.trim() || "default";
		handleToolEnvFieldChange(index, "type", `OAuth2:${normalizedProvider}`);
	};

	const handleAddToolEnvField = () => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: [...prev.toolEnvFields, createEmptyToolEnv()],
		}));
	};

	const handleRemoveToolEnvField = (index: number) => {
		setForm((prev) => ({
			...prev,
			toolEnvFields: prev.toolEnvFields.filter((_, i) => i !== index),
		}));
	};

	const handleSubmit = async () => {
		if (!form.name.trim() || !form.systemPrompt.trim()) {
			onValidationError("Nome e System Prompt são obrigatórios.");
			return;
		}

		if (!editingMcp && !form.slug.trim()) {
			onValidationError("Slug é obrigatório para novos MCPs.");
			return;
		}

		if (configValidation.error) {
			onValidationError(configValidation.error);
			return;
		}

		if (toolEnvValidation) {
			onValidationError(toolEnvValidation);
			return;
		}

		await onSubmit({
			slug: form.slug || undefined,
			name: form.name,
			category: form.category || "general",
			description: form.description || undefined,
			systemPrompt: form.systemPrompt,
			deliverableTypes: splitList(form.deliverableTypes),
			iconUrl: form.iconUrl || undefined,
			toolEnvFields: form.toolEnvFields,
			pricingModel: buildPricingModel(form),
			mcpServerConfig: configValidation.value as Record<string, any> | undefined,
		});
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{ className: styles.dialogPaper }}
		>
			<DialogTitle className={styles.dialogTitle}>
				<span>{editingMcp ? "Editar MCP" : "Novo MCP"}</span>
				<IconButton
					size="small"
					onClick={onClose}
				>
					<Icon
						path={mdiClose}
						size={0.8}
					/>
				</IconButton>
			</DialogTitle>
			<DialogContent
				className={styles.dialogContent}
				dividers
			>
				<div className={styles.formGrid}>
					<div className={styles.formSection}>
						<h4>Informações Básicas</h4>

						<TextField
							label="Slug (identificador único)"
							value={form.slug}
							onChange={handleFormChange("slug")}
							disabled={!!editingMcp}
							fullWidth
							size="small"
							required
							helperText={editingMcp ? "O slug não pode ser alterado" : "Ex: mcp-search, mcp-image"}
						/>

						<TextField
							label="Nome"
							value={form.name}
							onChange={handleFormChange("name")}
							fullWidth
							size="small"
							required
						/>

						<TextField
							label="Categoria"
							value={form.category}
							onChange={handleFormChange("category")}
							fullWidth
							size="small"
							helperText="Ex: general, marketing, development"
						/>

						<TextField
							label="Descrição"
							value={form.description}
							onChange={handleFormChange("description")}
							fullWidth
							size="small"
							multiline
							rows={4}
						/>

						<TextField
							label="URL do Ícone"
							value={form.iconUrl}
							onChange={handleFormChange("iconUrl")}
							fullWidth
							size="small"
						/>
					</div>

					<div className={styles.formSection}>
						<h4>System Prompt</h4>
						<TextField
							label="System Prompt"
							value={form.systemPrompt}
							onChange={handleFormChange("systemPrompt")}
							fullWidth
							size="small"
							multiline
							rows={6}
							required
							helperText="Instruções do sistema que definem o comportamento do MCP"
						/>
					</div>

					<div className={styles.formSection}>
						<h4>Configurações</h4>

						<TextField
							label="Tipos de Entregáveis"
							value={form.deliverableTypes}
							onChange={handleFormChange("deliverableTypes")}
							fullWidth
							size="small"
							helperText="Separados por vírgula. Ex: artigo, post, relatório"
						/>

						<TextField
							label="MCP Server Config (JSON)"
							value={form.mcpServerConfig}
							onChange={handleFormChange("mcpServerConfig")}
							fullWidth
							size="small"
							multiline
							rows={6}
							error={!!configValidation.error}
							helperText={configValidation.error || 'Formato esperado: { "mcpServers": { "server-name": { "command": "node" } } }'}
							InputProps={{
								style: { fontFamily: "'JetBrains Mono', monospace", fontSize: 13 },
							}}
						/>
					</div>

					<div className={styles.formSection}>
						<h4>Campos ENV da Ferramenta</h4>

						{form.toolEnvFields.length === 0 && (
							<p className={styles.envHint}>Nenhum campo ENV configurado. Adicione campos para exigir dados na contratação/edição.</p>
						)}

						{form.toolEnvFields.map((field, index) => {
							const mode = getToolEnvMode(field.type);
							return (
								<div
									key={`${field.envName}-${index}`}
									className={styles.envFieldCard}
								>
									<div className={styles.envFieldHeader}>
										<span>Campo #{index + 1}</span>
										<IconButton
											size="small"
											onClick={() => handleRemoveToolEnvField(index)}
										>
											<Icon
												path={mdiTrashCanOutline}
												size={0.7}
											/>
										</IconButton>
									</div>

									<div className={styles.envFieldGrid}>
										<TextField
											label="ENV Name"
											value={field.envName}
											onChange={(event) => handleToolEnvFieldChange(index, "envName", event.target.value)}
											size="small"
											helperText="Ex: OPENAI_API_KEY"
										/>

										<TextField
											label="Label"
											value={field.label}
											onChange={(event) => handleToolEnvFieldChange(index, "label", event.target.value)}
											size="small"
										/>

										<Select
											size="small"
											value={mode}
											onChange={(event) =>
												handleToolEnvTypeChange(
													index,
													event.target.value as "string" | "number" | "boolean" | "json" | "oauth2",
												)
											}
										>
											<MenuItem value="string">string</MenuItem>
											<MenuItem value="number">number</MenuItem>
											<MenuItem value="boolean">boolean</MenuItem>
											<MenuItem value="json">json</MenuItem>
											<MenuItem value="oauth2">OAuth2</MenuItem>
										</Select>

										{mode === "oauth2" && (
											<TextField
												label="OAuth2 Provider"
												value={getOAuthProvider(field.type)}
												onChange={(event) => handleOAuthProviderChange(index, event.target.value)}
												size="small"
												helperText='Gera tipo no formato "OAuth2:provider"'
											/>
										)}

										<TextField
											label="Descrição"
											value={field.description}
											onChange={(event) => handleToolEnvFieldChange(index, "description", event.target.value)}
											size="small"
											multiline
											rows={2}
										/>

										<FormControlLabel
											className={styles.envRequiredControl}
											control={
												<Checkbox
													size="small"
													checked={field.required}
													onChange={(event) => handleToolEnvFieldChange(index, "required", event.target.checked)}
												/>
											}
											label="Obrigatório"
										/>
									</div>
								</div>
							);
						})}

						<div className={styles.envActions}>
							<Button
								variant="outlined"
								onClick={handleAddToolEnvField}
								startIcon={
									<Icon
										path={mdiPlus}
										size={0.7}
									/>
								}
							>
								Adicionar Campo ENV
							</Button>
						</div>

						{toolEnvValidation && <p className={styles.envError}>{toolEnvValidation}</p>}
					</div>

					<div className={styles.formSection}>
						<h4>Modelo de Preço</h4>
						<div className={styles.inlineFields}>
							<TextField
								label="Junior"
								value={form.pricingJunior}
								onChange={handleFormChange("pricingJunior")}
								size="small"
								type="number"
								inputProps={{ step: "0.01", min: "0" }}
							/>
							<TextField
								label="Pleno"
								value={form.pricingPleno}
								onChange={handleFormChange("pricingPleno")}
								size="small"
								type="number"
								inputProps={{ step: "0.01", min: "0" }}
							/>
							<TextField
								label="Senior"
								value={form.pricingSenior}
								onChange={handleFormChange("pricingSenior")}
								size="small"
								type="number"
								inputProps={{ step: "0.01", min: "0" }}
							/>
						</div>
					</div>
				</div>
			</DialogContent>
			<DialogActions className={styles.dialogActions}>
				<Button
					onClick={onClose}
					disabled={saving}
				>
					Cancelar
				</Button>
				<Button
					variant="contained"
					onClick={handleSubmit}
					disabled={saving || !!configValidation.error || !!toolEnvValidation}
				>
					{saving ? (
						<CircularProgress
							size={18}
							color="inherit"
						/>
					) : editingMcp ? (
						"Salvar Alterações"
					) : (
						"Criar MCP"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
